/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect, useState } from 'react'
import { useForm, SubmitHandler, Controller } from 'react-hook-form'
import Select, { MultiValue } from 'react-select'

/* teste */
import { Input, InputGroup } from '@components'
import Button from '@components/Button'
import Modal from '@components/Modal'
import { successMessages } from '@constants/feedbackMessages'
import { Dialog } from '@headlessui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { editExamsSchema } from '@schemas/addexams.schema'
import { classesApi } from '@services/ClassesApi'
import { examsApi } from '@services/ExamsApi'
import { closeModal } from '@store/ducks/modal'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { inputIsValid } from '@utils/helpers'
import { getCssStyle, pixelsToRem } from '@utils/helpers/styleHelpers'

type Inputs = {
	name: string
	categoryId: string
	classes: MultiValue<{ value: string | number; label: string }>
}

const styles = {
	container:
		'relative inline-block align-bottom bg-white rounded-lg pl-10 pr-10 pt-6 pb-9 text-left overflow-hidden shadow-xl transform transition-all my-8 sm:align-middle',
	title: 'text-lg leading-6 font-medium text-gray-900',
	subTitle: 'text-gray-500 mt-1',
	buttonGroup: 'mt-7 flex gap-4 justify-end',
}

export default function EditExams() {
	const dispatch = useAppDispatch()

	const { modalInfo: id, modalName } = useAppSelector((state) => state.modal)

	const { data: dataClasses } = classesApi.useListQuery({ limit: 100000 })

	const [getExams, { data: exams }] = examsApi.useShowMutation()

	const [editExams, { isLoading, isSuccess }] = examsApi.useEditMutation()

	const {
		register,
		control,
		setValue,
		reset,
		handleSubmit,
		formState: { errors, touchedFields },
	} = useForm<Inputs>({
		mode: 'onBlur',
		resolver: yupResolver(editExamsSchema),
	})

	const onSubmit: SubmitHandler<Inputs> = (dataE) => {
		const dataModule = {
			name: dataE.name,
			categoryId: dataE.categoryId,
			classIds: dataE.classes.map((v) => v.value as number) || [],
		}

		editExams({
			id,
			...dataModule,
		})

		reset()
	}

	useEffect(() => {
		if (isSuccess) {
			dispatch(closeModal())
			dispatch(notificationCreators.show(successMessages.storedExams))
		}
	}, [isSuccess, dispatch])

	const [classes, setClasses] = useState<
		Array<{ option: string; value: string }>
	>([])

	useEffect(() => {
		const newClasses: typeof classes = []

		if (exams?.classes && exams.classes.length > 0) {
			exams.classes.forEach((e) => {
				newClasses.push({
					option: e.name,
					value: e.id,
				})
			})
		}

		setClasses(newClasses)
	}, [exams])

	useEffect(() => {
		setValue(
			'classes',
			classes?.map((e) => ({ value: e.value, label: e.option }))
		)
		if (exams) {
			setValue('name', exams.name)
		}
	}, [setValue, classes, exams])

	useEffect(() => {
		if (id && modalName === 'editExams') {
			getExams({
				id,
			})
		}
	}, [id, modalName, getExams])

	return (
		<Modal name='editExams'>
			<Dialog.Panel
				style={getCssStyle(
					{ name: 'overflowY', value: 'auto' },
					{ name: 'maxWidth', value: pixelsToRem(630) },
					{ name: 'maxHeight', value: pixelsToRem(600) },
					{ name: 'width', value: '100%' }
				)}
				className={`${styles.container} modal-scroll`}
			>
				<p className={styles.title}>Editar Simulado</p>
				<p className={styles.subTitle}>
					Preencha os campos abaixo para editar o simulado
				</p>

				<form onSubmit={handleSubmit(onSubmit)}>
					<InputGroup className='mt-4' label='Nome do Simulado' id='name'>
						<Input
							isValid={inputIsValid(errors.name, touchedFields.name)}
							{...register('name')}
							type='text'
						/>
					</InputGroup>

					<InputGroup label='Turmas' id='classes' className='mt-4'>
						<Controller
							control={control}
							name='classes'
							render={({
								field: { value, onBlur },
								fieldState: { isTouched, error },
							}) => {
								return (
									<Select
										onChange={(v) => setValue('classes', v)}
										options={
											dataClasses?.data
												? dataClasses.data.map((item) => ({
														value: item.id,
														label: item.name,
												  }))
												: []
										}
										value={value}
										isMulti
										onBlur={onBlur}
										styles={{
											input: (base) => ({
												...base,
												borderRadius: '6px',
												paddingLeft: '2.5px',
												paddingRight: '2.5px',
												paddingTop: '3.5px',
												paddingBottom: '3.5px',
											}),
											control: (controlStyles) => ({
												...controlStyles,
												borderColor: (() => {
													const isValid = inputIsValid(error, isTouched)

													if (typeof isValid === 'undefined') {
														return '#D1D5DB !important'
													}

													if (!isValid) {
														return '#F44545 !important'
													}

													return '#15CA71 !important'
												})(),
											}),
											option: (optionStyle, { isFocused }) => ({
												...optionStyle,
												backgroundColor: isFocused ? '#1673FA' : '#fff',
												color: isFocused ? '#fff' : '#000',
											}),
										}}
										placeholder=''
										classNamePrefix='react-select'
									/>
								)
							}}
						/>
					</InputGroup>

					<div className={styles.buttonGroup}>
						<Button
							type='button'
							onClick={() => {
								dispatch(closeModal())
							}}
							variant='outline'
							disabled={false}
						>
							Cancelar
						</Button>

						<Button type='submit' isLoading={isLoading} disabled={!modalName}>
							Editar Simulado
						</Button>
					</div>
				</form>
			</Dialog.Panel>
		</Modal>
	)
}
